import { getInvesterOwnership } from "../lib/hooks";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import Spinner from "./Spinner";

const DYNAMIC_COLORS = [
  "#d4af37",
  "#00a896",
  "#e07a5f",
  "#f4a261",
  "#457b9d",
  "#264653",
  "#a8dadc",
  "#1d3557",
  "#e63946",
];

interface OwnershipData {
  name: string;
  ownership: string;
}

interface PartnerOwnershipChartProps {
  showUserOnly?: boolean;
}

interface ParsedPartner {
  name: string;
  value: number;
  originalStr: string;
  color: string;
}

export default function PartnerOwnershipChart({ showUserOnly = false }: PartnerOwnershipChartProps) {
  const { data, loading } = getInvesterOwnership() as { data: OwnershipData[] | null; loading: boolean };

  if (loading) {
    return (
      <div style={S.card}>
        <div style={{ display: "flex", justifyContent: "center", padding: 32 }}>
          <Spinner />
        </div>
      </div>
    );
  }

  const validData = data ?? [];
  const totalPartnersCount = validData.length;

  const parsedPartners: ParsedPartner[] = validData.map((inv) => {
    const ownershipStr = inv.ownership || "";
    const numericPct = parseFloat(ownershipStr.replace("%", "")) || 0;
    return {
      name: inv.name || "Unknown",
      value: numericPct,
      originalStr: ownershipStr || "0.00%",
      color: "",
    };
  });

  parsedPartners.sort((a, b) => b.value - a.value);

  const finalChartData = parsedPartners.map((partner, index) => ({
    ...partner,
    color: DYNAMIC_COLORS[index % DYNAMIC_COLORS.length],
  }));

  // Split data evenly into left and right sides
  const halfCount = Math.ceil(finalChartData.length / 2);
  const leftSidePartners = finalChartData.slice(0, halfCount);
  const rightSidePartners = finalChartData.slice(halfCount);

  return (
    <div style={S.card}>
      <style>{`
        @media (max-width: 900px) {
          .ownership-layout {
            grid-template-columns: 1fr !important;
            grid-template-rows: auto auto auto !important;
          }
          .ownership-left-legend { order: 2 !important; display: grid !important; grid-template-columns: repeat(2, 1fr) !important; gap: 8px !important; }
          .ownership-chart { order: 1 !important; }
          .ownership-right-legend { order: 3 !important; display: grid !important; grid-template-columns: repeat(2, 1fr) !important; gap: 8px !important; }
        }
        @media (max-width: 480px) {
          .ownership-left-legend { grid-template-columns: 1fr !important; }
          .ownership-right-legend { grid-template-columns: 1fr !important; }
          .ownership-card { padding: 16px !important; }
        }
      `}</style>
      <div style={S.header}>
        <span style={S.mainTitle}>ShivSena Networks FUND</span>
        <h2 style={S.title}>PARTNER OWNERSHIP</h2>
        <div style={S.subtitleContainer}>
          <span style={S.subtitle}>EQUITY DISTRIBUTION</span>
          <span style={S.subtitleDivider}>•</span>
          <span style={S.subtitle}>2026</span>
        </div>
        <div style={S.goldLineContainer}>
          <div style={S.goldLine} />
        </div>
        <div style={S.badgeContainer}>
          <span style={S.badge}>
            <span style={S.badgeDot}>•</span> {totalPartnersCount} Active Partners <span style={S.badgeDot}>•</span>
          </span>
        </div>
      </div>

      <div style={S.chartLayout} className="ownership-layout">
        {/* Left Side Partners */}
        <div style={S.leftLegend} className="ownership-left-legend">
          {leftSidePartners.map((entry, index) => (
            <div key={`${entry.name}-left-${index}`} style={S.legendBox}>
              <div style={{ ...S.avatar, backgroundColor: entry.color }}>
                {entry.name ? entry.name[0] : "?"}
              </div>
              <div>
                <div style={S.partnerName}>{entry.name}</div>
                <div style={{ color: entry.color, fontSize: 12, fontWeight: 600 }}>
                  {entry.originalStr}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Center Pie Chart */}
        <div style={S.chartContainer} className="ownership-chart">
          <ResponsiveContainer width="100%" height={280}>
            <PieChart>
              <Pie
                data={finalChartData}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={100}
                paddingAngle={3}
                dataKey="value"
              >
                {finalChartData.map((entry, index) => (
                  <Cell key={`cell-${entry.name}-${index}`} fill={entry.color} stroke="rgba(0,0,0,0.3)" />
                ))}
              </Pie>
              <Tooltip
                contentStyle={{ backgroundColor: "#161b22", borderColor: "rgba(255,255,255,0.1)" }}
                itemStyle={{ color: "#fff" }}
              />
            </PieChart>
          </ResponsiveContainer>
          <div style={S.centerLabel}>
            <div style={S.circleClip}>
              <span style={S.centerText}>ShivSena Network</span>
            </div>
          </div>
        </div>

        {/* Right Side Partners */}
        <div style={S.rightLegend} className="ownership-right-legend">
          {rightSidePartners.map((entry, index) => (
            <div key={`${entry.name}-right-${index}`} style={S.legendBox}>
              <div style={{ ...S.avatar, backgroundColor: entry.color }}>
                {entry.name ? entry.name[0] : "?"}
              </div>
              <div>
                <div style={S.partnerName}>{entry.name}</div>
                <div style={{ color: entry.color, fontSize: 12, fontWeight: 600 }}>
                  {entry.originalStr}
                </div>
              </div>
            </div>
          ))}
        </div>

      </div>
    </div>
  );
}

const S: Record<string, React.CSSProperties> = {
  card: {
    background: "linear-gradient(145deg, #0d1117, #161b22)",
    border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: 12,
    padding: 32,
    boxShadow: "0 20px 40px rgba(0,0,0,0.4)",
    boxSizing: "border-box",
  },
  header: {
    textAlign: "center",
    marginBottom: 24,
  },
  mainTitle: {
    display: "block",
    fontSize: 10,
    color: "var(--text-muted)",
    letterSpacing: "0.2em",
    marginBottom: 4,
  },
  title: {
    display: "block",
    fontSize: "clamp(16px, 3vw, 22px)",
    fontWeight: "bold",
    color: "#f0f6fc",
    letterSpacing: "0.1em",
  },
  subtitleContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: 8,
    marginTop: 4,
  },
  subtitle: {
    fontSize: 10,
    color: "var(--text-muted)",
    letterSpacing: "0.2em",
  },
  subtitleDivider: {
    color: "var(--text-muted)",
    fontSize: 10,
  },
  goldLineContainer: {
    display: "flex",
    justifyContent: "center",
    margin: "12px 0",
  },
  goldLine: {
    width: 60,
    height: 1,
    background: "linear-gradient(90deg, transparent, #d4af37, transparent)",
  },
  badgeContainer: {
    display: "flex",
    justifyContent: "center",
  },
  badge: {
    fontSize: 10,
    color: "var(--text-muted)",
    letterSpacing: "0.1em",
  },
  badgeDot: {
    color: "#d4af37",
  },
  chartLayout: {
    display: "grid",
    gridTemplateColumns: "220px 1fr 220px",
    alignItems: "center",
    gap: 16,
  },
  chartContainer: {
    position: "relative",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  centerLabel: {
    position: "absolute",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    width: 120,
    height: 120,
    pointerEvents: "none",
  },
  circleClip: {
    width: "100%",
    height: "100%",
    borderRadius: "50%",
    overflow: "hidden",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    background: "#0d1117",
  },
  centerText: {
    fontSize: 11,
    fontWeight: 700,
    color: "#d4af37",
    letterSpacing: "0.05em",
    textAlign: "center",
  },
  leftLegend: {
    display: "flex",
    flexDirection: "column",
    gap: 10,
  },
  rightLegend: {
    display: "flex",
    flexDirection: "column",
    gap: 10,
  },
  legendBox: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    background: "rgba(255,255,255,0.02)",
    padding: "8px 12px",
    borderRadius: 6,
    border: "1px solid rgba(255,255,255,0.05)",
  },
  avatar: {
    width: 28,
    height: 28,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#fff",
    fontWeight: "bold",
    fontSize: 12,
    flexShrink: 0,
  },
  partnerName: {
    color: "#c9d1d9",
    fontSize: 13,
    fontWeight: 500,
    wordBreak: "break-word",
  },
};